import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '../../lib/utils';
import { useIsMobileActive } from '../../hooks/useIsMobileActive';

type DialogContentContextValue = {
  setDescriptionId: (id?: string) => void;
};

const DialogContentContext = React.createContext<DialogContentContextValue | null>(null);

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 z-50 bg-black/70', className)}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  hideCloseButton?: boolean;
  closeButtonClassName?: string;
  closeLabel?: string;
  'aria-describedby'?: string;
  /** Accessible title for screen readers when no visible DialogTitle is used */
  accessibleTitle?: string;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>((props, ref) => {
  const {
    className,
    children,
    hideCloseButton = false,
    closeButtonClassName,
    closeLabel = 'Close',
    'aria-describedby': ariaDescribedByProp,
    accessibleTitle = 'Dialog',
    onOpenAutoFocus,
    onCloseAutoFocus,
    ...contentProps
  } = props;
  const returnFocus = React.useRef<HTMLElement | null>(null);
  const mobile = useIsMobileActive();
  const [descriptionId, setDescriptionId] = React.useState<string | undefined>();
  const ariaDescribedBy = ariaDescribedByProp ?? descriptionId;

  return (
    <DialogContentContext.Provider value={{ setDescriptionId }}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 grid w-full max-w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] -translate-x-1/2 -translate-y-1/2 gap-4 overflow-y-auto overflow-x-hidden rounded-xl border border-border bg-background p-6 shadow-xl sm:p-8',
            className
          )}
          aria-describedby={ariaDescribedBy}
          {...contentProps}
          onCloseAutoFocus={(event) => {
            onCloseAutoFocus?.(event);
            // Controlled dialogs may be opened from a menu without a DialogTrigger.
            if (!event.defaultPrevented && returnFocus.current?.isConnected) {
              event.preventDefault();
              returnFocus.current.focus({ preventScroll: true });
            }
          }}
          onOpenAutoFocus={(event) => {
            returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
            onOpenAutoFocus?.(event);
            if (mobile) {
              event.preventDefault();
              // Keep the dialog announced and focus trapped without opening the keyboard.
              (event.target as HTMLElement).focus({ preventScroll: true });
            }
          }}
        >
          {/* Hidden title for accessibility - only used when no visible DialogTitle is present */}
          <DialogPrimitive.Title className="sr-only">{accessibleTitle}</DialogPrimitive.Title>
          {children}
          {!hideCloseButton ? (
            <DialogPrimitive.Close
              className={cn(
                'absolute right-4 top-4 rounded-full p-2 opacity-70 transition hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none',
                closeButtonClassName
              )}
            >
              <X className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only">{closeLabel}</span>
            </DialogPrimitive.Close>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogContentContext.Provider>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
);

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)} {...props} />
);

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, children, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  >
    {children}
  </DialogPrimitive.Title>
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, children, id: idProp, ...props }, ref) => {
  const context = React.useContext(DialogContentContext);
  const generatedId = React.useId();
  const id = idProp ?? generatedId;

  React.useEffect(() => {
    context?.setDescriptionId(id);
    return () => context?.setDescriptionId(undefined);
  }, [context, id]);

  return (
    <DialogPrimitive.Description
      ref={ref}
      id={id}
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    >
      {children}
    </DialogPrimitive.Description>
  );
});
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
};
